import React from 'react';
import styled, { css } from 'styled-components';
import day from 'dayjs';
import { LabelUppercase } from '../elements/Text';
import {
  CompanyResponse,
  LEARNER_STATUS,
  OfficeHoursResponse,
} from '../../api/admin';
import {
  createColumnHelper,
  OnChangeFn,
  RowSelectionState,
} from '@tanstack/react-table';
import { SmallPortalTooltip } from '../../components/Tooltip';
import Checkbox from '../../components/elements/Checkbox';
import AssignLearnersToMentorModal, {
  convertTimestampToLocalTime,
} from './Modals/AssignLearnersToMentorModal';
import dayjs from 'dayjs';
import {
  TooltipContent,
  TooltipTitle,
  TooltipDetail,
  TooltipDetailHighlight,
} from './Progress/Tooltip';
import {
  useGetCompany,
  useGetCompanyOfficeHours,
} from '../../hooks/use-hydra/admin';
import Table from '../Table/CompactTable';
import CollapsibleSection, {
  CollapsibleSectionHeader,
  CollapsibleSectionLabel,
  CollapsibleSectionFooter,
  CollapsibleSectionFooterButton,
} from './CollapsibleSection';
import { calculateLearningPlanProgressFromPlan } from './Progress';
import {
  ProgressBarContainer,
  ProgressBarFill,
  ProgressCellContent,
} from './Progress/ProgressCell';
import UpdateMentorCohortAvailabilityModal from './Modals/UpdateMentorCohortAvailability';

type Cohort = CompanyResponse['cohorts'][number];

export default function Cohort({
  cohort,
  allCompanyLearners,
  companyId,
  isFirstCohort,
}: {
  cohort: Cohort;
  allCompanyLearners: CompanyResponse['learners'];
  companyId: number;
  isFirstCohort: boolean;
}) {
  const { data: company } = useGetCompany(companyId);
  const { data: officeHours } = useGetCompanyOfficeHours(companyId);

  const humanReadableTimeSince = day
    .utc(cohort.startDate)
    .fromNow()
    .replace(/^\w/, (c) => c.toUpperCase());

  const timezoneOffset = dayjs().utcOffset();
  const timezoneHours = timezoneOffset / 60;
  const timezoneString =
    timezoneHours >= 0 ? `GMT+${timezoneHours}` : `GMT${timezoneHours}`;

  const [tableSelection, setTableSelection] = React.useState<
    Record<string, boolean>
  >({});

  const [isAssignMentorsModalOpen, setIsAssignMentorsModalOpen] =
    React.useState(false);
  const [
    isUpdateMentorAvailabilityModalOpen,
    setIsUpdateMentorAvailabilityModalOpen,
  ] = React.useState(false);

  const [expanded, setExpanded] = React.useState(isFirstCohort);

  const sortedLearners = React.useMemo(() => {
    return cohort.learners.sort((a, b) => {
      if (!a.onboardedAt && b.onboardedAt) return -1;
      if (a.onboardedAt && !b.onboardedAt) return 1;

      if (!a.onboardedAt && !b.onboardedAt) {
        return (
          dayjs.utc(b.invitedAt).valueOf() - dayjs.utc(a.invitedAt).valueOf()
        );
      }

      // At this point, we know both have onboardedAt values
      const aOnboarded = dayjs.utc(a.onboardedAt).valueOf();
      const bOnboarded = dayjs.utc(b.onboardedAt).valueOf();
      return bOnboarded - aOnboarded;
    });
  }, [cohort.learners]);

  const selectedLearners = sortedLearners.filter(
    (learner) => tableSelection[learner.sessionId?.toString() ?? '']
  );
  const numSelected = selectedLearners.length;
  const selectedLearnersWithMentors = selectedLearners.filter(
    (learner) => learner.mentor
  );

  const canAssignMentors =
    numSelected > 0 &&
    dayjs.utc(cohort.endDate).isAfter(dayjs.utc()) &&
    selectedLearnersWithMentors.length === 0;

  const assignMentorButtonTooltip =
    numSelected <= 0 ? (
      <TooltipContent>
        <TooltipDetail>No learners selected</TooltipDetail>
      </TooltipContent>
    ) : selectedLearnersWithMentors.length > 0 ? (
      <TooltipContent>
        <TooltipDetail>
          Cannot assign mentor to learners with mentors
        </TooltipDetail>

        {selectedLearnersWithMentors.map((learner, index) => (
          <TooltipDetail
            style={{ marginTop: index === 0 ? 8 : 0 }}
            key={learner.sessionId}
          >
            <TooltipDetailHighlight>
              {index + 1}. {learner.name}
            </TooltipDetailHighlight>
          </TooltipDetail>
        ))}
      </TooltipContent>
    ) : dayjs.utc(cohort.endDate).isBefore(dayjs.utc()) ? (
      'Cohort is in the past'
    ) : (
      ''
    );

  return (
    <CollapsibleSection
      headerLeft={
        <>
          <CollapsibleSectionHeader>
            {day.utc(cohort.startDate).format('DD MMMM YYYY')} to{' '}
            {day.utc(cohort.endDate).format('DD MMMM YYYY')}
          </CollapsibleSectionHeader>
        </>
      }
      headerRight={
        <CollapsibleSectionLabel>
          Started {humanReadableTimeSince}
        </CollapsibleSectionLabel>
      }
      expanded={expanded}
      onToggle={() => setExpanded((prev) => !prev)}
    >
      <ContentWrapper>
        <ContentMetadataWrapper>
          {/* Metadata above the table */}
          <LabelUppercase style={{ marginTop: 8, opacity: 0.7 }}>
            All times shown in {timezoneString}
          </LabelUppercase>
          {company?.settings?.KICK_OFF &&
            (cohort.kickOffTimes && cohort.kickOffTimes.length > 0 ? (
              <LabelUppercase style={{ marginTop: 8 }}>
                <span style={{ opacity: 0.7 }}>Kick-off times:</span>{' '}
                {cohort.kickOffTimes
                  .map((time) =>
                    dayjs.utc(time).local().format('DD/MM/YY HH:mm')
                  )
                  .join(', ')}{' '}
              </LabelUppercase>
            ) : (
              <LabelUppercase style={{ marginTop: 8, opacity: 0.6 }}>
                No kick-off times configured for this cohort
              </LabelUppercase>
            ))}
          {cohort.officeHourTimes && cohort.officeHourTimes.length > 0 ? (
            <LabelUppercase style={{ marginTop: 8 }}>
              <span style={{ opacity: 0.7 }}>Office hours:</span>{' '}
              {cohort.officeHourTimes
                .map((time) => convertTimestampToLocalTime(time))
                .join(', ')}{' '}
            </LabelUppercase>
          ) : (
            <LabelUppercase style={{ marginTop: 8, opacity: 0.6 }}>
              No office hours configured for this cohort
            </LabelUppercase>
          )}
        </ContentMetadataWrapper>
        <CohortLearnersTable
          cohortId={cohort.cycleId}
          learners={sortedLearners}
          learnerSelection={tableSelection}
          setLearnerSelection={setTableSelection}
          officeHours={officeHours ?? null}
          assignMentorButtonTooltip={assignMentorButtonTooltip}
          setIsAssignMentorsModalOpen={setIsAssignMentorsModalOpen}
          canAssignMentors={canAssignMentors}
          allCompanyLearners={allCompanyLearners}
          setIsUpdateMentorAvailabilityModalOpen={
            setIsUpdateMentorAvailabilityModalOpen
          }
        />
      </ContentWrapper>

      <AssignLearnersToMentorModal
        isOpen={isAssignMentorsModalOpen}
        onClose={() => setIsAssignMentorsModalOpen(false)}
        companyId={companyId}
        selectedSessionIds={selectedLearners.map(
          (learner) => learner.sessionId ?? -1
        )}
        cycleId={cohort.cycleId}
      />

      <UpdateMentorCohortAvailabilityModal
        isOpen={isUpdateMentorAvailabilityModalOpen}
        onClose={() => setIsUpdateMentorAvailabilityModalOpen(false)}
        companyId={companyId}
        cohortId={cohort.cycleId}
      />
    </CollapsibleSection>
  );
}

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const ContentMetadataWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
`;

const columnHelper = createColumnHelper<Cohort['learners'][number]>();

const StatusPill = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;

  ${({ status, theme }) => {
    switch (status) {
      case LEARNER_STATUS.INVITED:
        return css`
          background-color: ${theme.colors.secondary};
          color: ${theme.colors.buttonTextColor};
        `;
      case LEARNER_STATUS.ONBOARDED:
        return css`
          background-color: ${theme.colors.bgDefault};
          color: ${theme.colors.textPrimary};
        `;
      case LEARNER_STATUS.ACTIVE:
        return css`
          background-color: ${theme.colors.primary};
          color: ${theme.colors.buttonTextColor};
        `;
      case LEARNER_STATUS.INACTIVE:
        return css`
          background-color: ${theme.colors.secondary};
          color: ${theme.colors.textRegular};
        `;
      case LEARNER_STATUS.INVITE_EXPIRED:
        return css`
          background-color: ${theme.colors.bgDefaultSecondary};
          color: ${theme.colors.textRegular};
        `;
      case LEARNER_STATUS.COHORT_ENDED:
        return css`
          background-color: ${theme.colors.bgDefaultSecondary};
          color: ${theme.colors.textRegular};
        `;
      default:
        return css`
          background-color: ${theme.colors.bgDefaultSecondary};
          color: ${theme.colors.textRegular};
        `;
    }
  }}
`;

const StatusCell = (props: {
  getValue: () => string;
  row: {
    original: {
      status: string;
      invitedAt: Date | null;
      sessionId: number;
      onboardedAt: Date | null;
    };
  };
}) => {
  const status = props.row.original.status;
  const invitedAt = props.row.original?.invitedAt;
  const onboardedAt = props.row?.original?.onboardedAt;

  const sessionId = props.row.original.sessionId;

  // Get timezone offset for display
  const timezoneOffset = dayjs().utcOffset();
  const timezoneHours = timezoneOffset / 60;
  const timezoneString =
    timezoneHours >= 0 ? `GMT+${timezoneHours}` : `GMT${timezoneHours}`;

  const uniqueId = `status-tooltip-${sessionId}`;

  return (
    <>
      <StatusPill status={status} data-tooltip-id={uniqueId}>
        {status}
      </StatusPill>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${uniqueId}"]`}>
        <TooltipContent>
          {invitedAt ? (
            <TooltipDetail>
              Invited at{' '}
              {dayjs.utc(invitedAt).local().format('YYYY-MM-DD HH:mm')}{' '}
              {timezoneString}
            </TooltipDetail>
          ) : (
            ''
          )}
          {onboardedAt ? (
            <TooltipDetail>
              Onboarded at{' '}
              {dayjs.utc(onboardedAt).local().format('YYYY-MM-DD HH:mm')}{' '}
              {timezoneString}
            </TooltipDetail>
          ) : (
            ''
          )}
        </TooltipContent>
      </SmallPortalTooltip>
    </>
  );
};

export const MentorCell = (props: {
  getValue: () => null | {
    name: string;
    email: string;
    officeHourGroupMemberId: number | null;
    officeHourGroupId: number | null;
  };
}) => {
  const mentor = props.getValue();
  if (!mentor) {
    return '-';
  }
  const mentorName = mentor.name;
  const mentorEmail = mentor.email;
  const mentorOfficeHourGroupMemberId = mentor.officeHourGroupMemberId;
  return (
    <>
      <div className={`mentor-${mentorOfficeHourGroupMemberId}`}>
        {mentorName}
      </div>
      <SmallPortalTooltip
        anchorSelect={`.mentor-${mentorOfficeHourGroupMemberId}`}
      >
        <TooltipContent>
          <TooltipDetail>{mentorEmail}</TooltipDetail>
        </TooltipContent>
      </SmallPortalTooltip>
    </>
  );
};

const createLearnersColumns = (
  allLearners: Cohort['learners'],
  officeHours: OfficeHoursResponse | null,
  cohortId: number,
  allCompanyLearners: CompanyResponse['learners']
) => {
  return [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          isChecked={table.getIsAllRowsSelected()}
          isIndeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          isChecked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      size: 48,
      meta: {
        isCentered: true,
        styles: {
          width: '48px',
          minWidth: '48px',
          maxWidth: '48px',
        },
      },
    }),
    columnHelper.display({
      id: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <>
          <div className={`learner-${row.original.sessionId}`}>
            {row.original.name ?? 'N/A'}
          </div>
          <SmallPortalTooltip
            anchorSelect={`.learner-${row.original.sessionId}`}
          >
            <TooltipContent>
              <TooltipDetail>{row.original.email}</TooltipDetail>
              {allCompanyLearners.find(
                (learner) => learner.id === row.original.learnerId
              )?.onboardingAnswers.role && (
                <TooltipDetail>
                  Role:{' '}
                  {
                    allCompanyLearners.find(
                      (learner) => learner.id === row.original.learnerId
                    )?.onboardingAnswers.role
                  }
                </TooltipDetail>
              )}
            </TooltipContent>
          </SmallPortalTooltip>
        </>
      ),
      size: 200,
      meta: {
        styles: {
          width: '200px',
          minWidth: '200px',
          maxWidth: '200px',
        },
      },
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: StatusCell,
      meta: {
        styles: {
          minWidth: '150px',
          maxWidth: '150px',
        },
      },
    }),
    columnHelper.accessor('lastActiveAt', {
      header: () => 'Last Active',
      cell: (info) => {
        const lastActiveDate = info.getValue();

        if (!lastActiveDate) {
          return (
            <div
              style={{ textAlign: 'center', fontStyle: 'italic', opacity: 0.6 }}
            >
              -
            </div>
          );
        }

        const uniqueId = `last-active-tooltip-${info.row.id}`;

        return (
          <div data-tooltip-id={uniqueId}>
            {dayjs(lastActiveDate).fromNow()}
            <SmallPortalTooltip
              anchorSelect={`[data-tooltip-id="${uniqueId}"]`}
            >
              <TooltipContent>
                <TooltipTitle>Last Active</TooltipTitle>
                <TooltipDetail>
                  {dayjs(lastActiveDate).format('MMM D, YYYY HH:mm UTC')}
                </TooltipDetail>
              </TooltipContent>
            </SmallPortalTooltip>
          </div>
        );
      },
      meta: {
        styles: {
          minWidth: '180px',
          maxWidth: '180px',
        },
      },
    }),
    columnHelper.display({
      id: 'progress',
      header: 'Progress',
      cell: ({ row }) => {
        const sortedSubmissions = row.original.progress.submissions.sort(
          (a, b) =>
            new Date(b.submissionDate).getTime() -
            new Date(a.submissionDate).getTime()
        );
        const learner = allCompanyLearners.find(
          (learner) => learner.id === row.original.learnerId
        );
        const learningPlan = learner?.learningPlan;
        if (!learningPlan) return '-';

        const progressPercentage =
          calculateLearningPlanProgressFromPlan(learningPlan);

        if (!progressPercentage) return '-';

        return (
          <div
            data-tooltip-id={row.original.sessionId.toString()}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <ProgressCellContent
              className={`progress-${row.original.sessionId}`}
            >
              <ProgressBarContainer>
                <ProgressBarFill progress={progressPercentage} />
              </ProgressBarContainer>
            </ProgressCellContent>
            <SmallPortalTooltip
              anchorSelect={`[data-tooltip-id="${row.original.sessionId.toString()}"]`}
            >
              <TooltipContent>
                <TooltipTitle>Latest Plan Progress</TooltipTitle>
                <TooltipDetail>{progressPercentage}%</TooltipDetail>
                {sortedSubmissions.length > 0 && (
                  <>
                    <TooltipTitle style={{ marginTop: 8 }}>
                      Latest Submissions
                    </TooltipTitle>
                    {sortedSubmissions.map((s, index) => (
                      <TooltipDetail key={`${s.submissionId}-${index}`}>
                        {index + 1}.{' '}
                        <TooltipDetailHighlight>
                          {s.name}
                        </TooltipDetailHighlight>{' '}
                        - {dayjs.utc(s.submissionDate).fromNow()}
                      </TooltipDetail>
                    ))}
                  </>
                )}
              </TooltipContent>
            </SmallPortalTooltip>
          </div>
        );
      },
      meta: {
        styles: {
          minWidth: '150px',
          textAlign: 'right',
        },
      },
    }),
    columnHelper.accessor('mentor', {
      id: 'mentor',
      header: 'Mentor',
      cell: MentorCell,
      meta: {
        styles: {
          minWidth: '200px',
        },
      },
    }),
    columnHelper.display({
      id: 'office-hour',
      header: `Office Hours`,
      cell: ({ row }) => {
        const officeHoursGroup = officeHours?.officeHoursGroups.find((oh) =>
          oh.members.find(
            (m) =>
              m.learnerId === row.original.learnerId &&
              oh.cohort.id === cohortId
          )
        );
        if (!officeHoursGroup || !officeHoursGroup.officeHours.length)
          return '-';

        const firstOfficeHour = officeHoursGroup.officeHours[0];
        const fromNow = dayjs.utc(firstOfficeHour.timestamp).fromNow();

        return (
          <>
            <div className={`office-hour-${row.original.sessionId}`}>
              {convertTimestampToLocalTime(firstOfficeHour.timestamp)}
            </div>
            <SmallPortalTooltip
              anchorSelect={`.office-hour-${row.original.sessionId}`}
            >
              <TooltipContent>
                <TooltipDetail>{fromNow}</TooltipDetail>
              </TooltipContent>
            </SmallPortalTooltip>
          </>
        );
      },
      meta: {
        styles: {
          minWidth: '200px',
        },
      },
    }),
    columnHelper.display({
      id: 'office-hour-group-members',
      header: 'Office Hour Group Members',
      cell: ({ row }) => {
        const officeHoursGroup = officeHours?.officeHoursGroups.find(
          (oh) =>
            oh.cohort.id === cohortId &&
            oh.members.find((m) => m.learnerId === row.original.learnerId)
        );
        if (!officeHoursGroup) return '-';

        const restLearners = officeHoursGroup.members
          .filter((m) => m.learnerId !== row.original.learnerId)
          .map((m) => allLearners.find((l) => l.learnerId === m.learnerId))
          .filter((l) => l !== undefined);
        if (restLearners.length === 0) return '-';

        const names = restLearners.map((l) => l?.name ?? 'N/A').join(', ');

        return (
          <>
            <div className={`group-members-${row.original.sessionId}`}>
              {names}
            </div>
            <SmallPortalTooltip
              anchorSelect={`.group-members-${row.original.sessionId}`}
            >
              <TooltipContent>
                {restLearners.map((l) => (
                  <TooltipDetail key={`${l?.learnerId}-${l?.email}`}>
                    {l?.email ?? 'N/A'}
                  </TooltipDetail>
                ))}
              </TooltipContent>
            </SmallPortalTooltip>
          </>
        );
      },
      meta: {
        styles: {
          minWidth: '300px',
        },
      },
    }),
    columnHelper.display({
      id: 'onboarding-kickoff',
      header: `Chosen Kick Off`,
      cell: ({ row }) => {
        const kickOffTime = row.original.onboardingAnswers.kickOffTime;
        if (!kickOffTime) return '-';

        const timeObj = dayjs.utc(kickOffTime).local();
        const fromNow = dayjs.utc(kickOffTime).fromNow();

        return (
          <>
            <div className={`kickoff-${row.original.sessionId}`}>
              {timeObj.format('DD/MM/YY HH:mm')}
            </div>
            <SmallPortalTooltip
              anchorSelect={`.kickoff-${row.original.sessionId}`}
            >
              <TooltipContent>
                <TooltipDetail>{fromNow}</TooltipDetail>
              </TooltipContent>
            </SmallPortalTooltip>
          </>
        );
      },
      meta: {
        styles: {
          minWidth: '200px',
        },
      },
    }),
  ];
};

const CohortTableWrapper = styled.div<{ enableVirtualization?: boolean }>`
  .table-scroll-wrapper {
    max-height: 50vh;
    ${({ enableVirtualization }) =>
      enableVirtualization &&
      css`
        height: 50vh;
      `}
  }
  .cohort-table {
    th,
    td {
      &:last-child {
        border-right: none !important;
      }
      &:first-child {
        border-left: none !important;
      }
    }

    tbody > tr:last-child > td {
      border-bottom: none;
    }

    th {
      border-radius: 0 !important;
    }
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const CohortLearnersTable = ({
  learners,
  learnerSelection,
  setLearnerSelection,
  officeHours,
  cohortId,
  assignMentorButtonTooltip,
  setIsAssignMentorsModalOpen,
  canAssignMentors,
  allCompanyLearners,
  setIsUpdateMentorAvailabilityModalOpen,
}: {
  learners: Cohort['learners'];
  learnerSelection: Record<string, boolean>;
  setLearnerSelection: OnChangeFn<RowSelectionState>;
  cohortId: number;
  officeHours: OfficeHoursResponse | null;
  assignMentorButtonTooltip: string | React.ReactNode;
  setIsAssignMentorsModalOpen: (isOpen: boolean) => void;
  canAssignMentors: boolean;
  allCompanyLearners: CompanyResponse['learners'];
  setIsUpdateMentorAvailabilityModalOpen: (isOpen: boolean) => void;
}) => {
  const columns = React.useMemo(
    () =>
      createLearnersColumns(
        learners,
        officeHours,
        cohortId,
        allCompanyLearners
      ),
    [learners, officeHours, cohortId, allCompanyLearners]
  );

  const numSelected = learners.filter(
    (learner) => learnerSelection[learner.sessionId.toString()]
  ).length;

  const shouldVirtualize = learners.length > 20;

  return (
    <CohortTableWrapper enableVirtualization={shouldVirtualize}>
      <Table
        data={learners}
        columns={columns}
        getRowId={(row) => row.sessionId.toString()}
        rowSelection={learnerSelection}
        onRowSelectionChange={setLearnerSelection}
        initialPinning={{
          left: ['select', 'name'],
        }}
        shouldPinHeaderRow
        enableVirtualization={shouldVirtualize}
        virtualizationRowHeight={50}
        className="cohort-table"
        footer={
          <CollapsibleSectionFooter>
            <CollapsibleSectionLabel>
              {learners.length} {learners.length === 1 ? 'Learner' : 'Learners'}
              {numSelected > 0 && <>. {numSelected} selected</>}
            </CollapsibleSectionLabel>
            <Row>
              <CollapsibleSectionFooterButton
                label="Assign Mentor"
                onClick={() => setIsAssignMentorsModalOpen(true)}
                faIcon="fa-solid fa-user-pen"
                isDisabled={!canAssignMentors}
                tooltip={assignMentorButtonTooltip}
                showIconBeforeLabel
              />
              <CollapsibleSectionFooterButton
                label="Update Mentor Availability"
                onClick={() => setIsUpdateMentorAvailabilityModalOpen(true)}
                faIcon="fa-solid fa-calendar-days"
                showIconBeforeLabel
              />
            </Row>
          </CollapsibleSectionFooter>
        }
      />
    </CohortTableWrapper>
  );
};
